import type { NastavitveFcVc as Nastavitve, PredlogFcVc, VrsticaFcVc } from '@koda/shared';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api';
import { decimalno, znesek } from '../lib/format';
import { Lupa } from '../sestavni/kazalniki-skupno';

/** Izbire deleža: 0–100 % v korakih po 5 %. */
const DELEZI = Array.from({ length: 21 }, (_, i) => (i * 5) / 100);

/** Delež 0–1 kot odstotek brez decimalk, npr. »35 %«. */
const odstotek = (delez: string): string => `${decimalno(String(Math.round(Number(delez) * 100)), 0)} %`;

/** Vir veljavnega deleža, kot ga navodilo zahteva (4.2). */
function vir(v: VrsticaFcVc): string {
  if (v.razresitev.vrsta === 'lastna') return v.nastavitev?.vir === 'suggested' ? 'lastna nastavitev (iz predloga)' : 'lastna nastavitev';
  if (v.razresitev.vrsta === 'podedovano') return `podedovano iz ${v.razresitev.od ?? ''}`;
  return 'nerazvrščeno';
}

/** Zanesljivost predloga; »visoka« je visoka po statistični metodi, ne računovodska gotovost (7.3). */
const ZANESLJIVOST: Record<PredlogFcVc['zanesljivost'], string> = {
  high: 'visoka (statistično)',
  medium: 'srednja',
  low: 'nizka',
  insufficient_data: 'premalo podatkov',
};

/** Kratek zapis predloga, npr. »VC 100 %« ali »fiksen«. */
function predlogKratko(p: PredlogFcVc): string {
  if (p.predlog === null) return 'ni predloga';
  return p.kategorija === 'fixed' ? 'fiksen (VC 0 %)' : `VC ${odstotek(p.predlog)}`;
}

/** Razlog predloga po determinističnih predlogah (7.4) in odločitvi B, brez statističnih izrazov. */
function razlogPredloga(p: PredlogFcVc): string {
  if (p.redki) {
    return `strošek je knjižen le v ${String(p.n)} od ${String(p.mesecev)} mesecev (npr. četrtletno ali letno); predlagamo fiksen, preverite`;
  }
  if (p.kategorija === 'variable') {
    const e = Number(p.e);
    return e > 1
      ? `strošek raste hitreje od prihodkov (elastičnost ${decimalno(String(e), 2)})`
      : 'strošek sledi prihodkom (povezava močna), raste približno enako hitro kot prihodki';
  }
  if (p.stalniPrihodki) {
    return p.kategorija === 'fixed'
      ? 'prihodki se v obdobju skoraj niso spreminjali, zato povezave s prihodki ni mogoče oceniti; znesek je stabilen, predlagamo fiksen'
      : 'prihodki se v obdobju skoraj niso spreminjali, zato povezave s prihodki ni mogoče oceniti; določi ročno';
  }
  if (p.kategorija === 'mixed') return 'strošek deloma sledi prihodkom';
  if (p.kategorija === 'fixed') return `strošek je prisoten v ${String(p.n)} od ${String(p.mesecev)} mesecev, znesek je stabilen, s prihodki ni povezan`;
  return 'iz zgodovine ni mogoče oceniti; določi ročno';
}

/** Mere za skrbnika: n, e, R², rCV. Elastičnost je koeficient (npr. 1,22), ne odstotek. */
function merePredloga(p: PredlogFcVc): string {
  const dec = (v: string | null, k: number): string => (v === null ? '—' : decimalno(v, k));
  return `n ${String(p.n)}/${String(p.mesecev)} · e ${dec(p.e, 2)} · R² ${dec(p.r2, 3)} · rCV ${dec(p.rcv, 3)}`;
}

/**
 * Predlog že velja: veljavni delež (lastni ali podedovani) je enak predlogu.
 * Takrat gumba »Prevzemi predlog« ni, da ne nastane nepotrebna 3-mestna
 * izjema, ki bi prekinila dedovanje od 2-mestnega konta.
 */
function velja(v: VrsticaFcVc): boolean {
  const predlog = v.predlog?.predlog ?? null;
  return predlog !== null && v.razresitev.delez !== null && Number(v.razresitev.delez) === Number(predlog);
}

/**
 * Oznaka odstopanja predloga od veljavnega deleža (7.6). Prejšnji predlogi se
 * ne hranijo: »se je spremenil« samo, kadar je veljavna nastavitev prevzet predlog.
 */
function oznakaOdstopanja(v: VrsticaFcVc): string {
  return v.razresitev.vrsta === 'lastna' && v.nastavitev?.vir === 'suggested' ? 'predlog se je spremenil' : 'predlog odstopa od veljavne nastavitve';
}

/**
 * Nastavitve FC/VC podjetja (docs/ARHITEKTURA.md, 6.3, Občutljivost): delež
 * variabilnih stroškov po kontu naše sheme. 2-mestni konto velja za vse svoje
 * 3-mestne podkonte; 3-mestna nastavitev je izjema, ki jo je mogoče odstraniti
 * (konto spet deduje). Privzetih deležev ni – brez nastavitve je strošek
 * nerazvrščen in v izračunih šteje kot fiksen. Predlog iz zgodovine je samo
 * predlog: prevzame se z gumbom (vir »iz predloga«), nikoli sam. Samo za skrbnika.
 */
export function NastavitveFcVc(): JSX.Element {
  const { id = '' } = useParams();
  const [nazivPodjetja, setNazivPodjetja] = useState('');
  const [nastavitve, setNastavitve] = useState<Nastavitve | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);
  const [vse, setVse] = useState(false);
  const [odprti, setOdprti] = useState<Set<string>>(new Set());

  // Zastareli odgovori se zavržejo: vsako branje dobi zaporedno številko in
  // prikaže se samo zadnje; ob menjavi podjetja se stanje počisti, odgovori
  // prejšnjega podjetja pa ne pridejo več na zaslon.
  const zadnjeBranje = useRef(0);
  const odprtoPodjetje = useRef(id);
  const osvezi = useCallback((): Promise<void> => {
    const branje = ++zadnjeBranje.current;
    return api
      .nastavitveFcVc(id)
      .then((n) => {
        if (branje === zadnjeBranje.current) setNastavitve(n);
      })
      .catch((e: unknown) => {
        if (branje === zadnjeBranje.current) {
          setNapaka(e instanceof Error ? e.message : 'Nastavitev ni bilo mogoče prebrati.');
        }
      });
  }, [id]);
  useEffect(() => {
    odprtoPodjetje.current = id;
    setNastavitve(null);
    setNapaka(null);
    setShranjujem(false);
    setOdprti(new Set());
    void osvezi();
    return () => {
      zadnjeBranje.current += 1;
    };
  }, [id, osvezi]);
  useEffect(() => {
    let velja = true;
    setNazivPodjetja('');
    api
      .podjetja()
      .then((vsa) => {
        if (velja) setNazivPodjetja(vsa.find((p) => p.id === id)?.naziv ?? '');
      })
      .catch(() => {
        /* naziv ni nujen za delovanje strani */
      });
    return () => {
      velja = false;
    };
  }, [id]);

  /**
   * Shrani ali odstrani nastavitev in nato prebere pregled znova. Izbire so
   * zaklenjene, dokler novi pregled ni na zaslonu, zato se spremembe ne
   * prehitevajo. Če se medtem odpre drugo podjetje, se izid zavrže.
   */
  const spremeni = (delo: Promise<unknown>): void => {
    const podjetje = id;
    const jeTrenutno = (): boolean => odprtoPodjetje.current === podjetje;
    setShranjujem(true);
    setNapaka(null);
    delo
      .then(() => (jeTrenutno() ? osvezi() : undefined))
      .catch((e: unknown) => {
        if (jeTrenutno()) setNapaka(e instanceof Error ? e.message : 'Nastavitve ni bilo mogoče shraniti.');
      })
      .finally(() => {
        if (jeTrenutno()) setShranjujem(false);
      });
  };
  /** Izbira v seznamu: prazna vrednost odstrani nastavitev (2-mestni: nerazvrščen, 3-mestni: dedovanje). */
  const nastavi = (konto: string, izbira: string): void => {
    spremeni(izbira === '' ? api.odstraniDelezFcVc(id, konto) : api.shraniDelezFcVc(id, konto, izbira));
  };
  /** Prevzem predloga: nastavitev konta z virom »iz predloga«. */
  const prevzemi = (konto: string, predlog: string): void => {
    spremeni(api.shraniDelezFcVc(id, konto, String(Number(predlog)), 'suggested'));
  };

  const vidne = (nastavitve?.vrstice ?? []).filter((v) => vse || v.vOknu);
  const dvomestni = vidne.filter((v) => v.raven === 2);
  const leto = nastavitve?.zadnjeCeloLeto ?? null;
  const n = nastavitve?.nerazvrsceniZadnjeCeloLeto ?? null;

  const vrstica = (v: VrsticaFcVc): JSX.Element => {
    const podkonti = vidne.filter((p) => p.nadrejeni === v.konto);
    const odprt = odprti.has(v.konto);
    const nadrejeni = v.nadrejeni === null ? undefined : nastavitve?.vrstice.find((x) => x.konto === v.nadrejeni);
    const brezLastne =
      v.raven === 2
        ? 'nerazvrščeno'
        : nadrejeni?.nastavitev != null
          ? `podedovano (${odstotek(nadrejeni.nastavitev.delez)})`
          : 'podedovano (nerazvrščeno)';
    return (
      <tr key={v.konto} className={v.raven === 3 ? 'podkonto' : undefined}>
        <td>
          {v.raven === 2 && podkonti.length > 0 ? (
            <button
              type="button"
              className="razsiri"
              aria-expanded={odprt}
              aria-label={`${odprt ? 'Skrij' : 'Pokaži'} 3-mestne konte ${v.konto}`}
              onClick={() => {
                const novi = new Set(odprti);
                if (odprt) novi.delete(v.konto);
                else novi.add(v.konto);
                setOdprti(novi);
              }}
            >
              <span aria-hidden="true">{odprt ? '−' : '+'}</span>
              <code>{v.konto}</code>
            </button>
          ) : (
            <code className={v.raven === 3 ? 'zamik' : undefined}>{v.konto}</code>
          )}
        </td>
        <td>{v.naziv}</td>
        <td className="stevilka">{v.znesekLeta === null ? '—' : znesek(v.znesekLeta)}</td>
        <td>
          <select
            aria-label={`Variabilni delež ${v.konto}`}
            value={v.nastavitev?.delez === undefined ? '' : String(Number(v.nastavitev.delez))}
            disabled={shranjujem}
            onChange={(e) => {
              nastavi(v.konto, e.target.value);
            }}
          >
            <option value="">{brezLastne}</option>
            {DELEZI.map((d) => (
              <option key={d} value={String(d)}>
                {odstotek(String(d))}
              </option>
            ))}
          </select>
        </td>
        <td className="stevilka">
          {v.razresitev.delez === null ? '—' : odstotek(String(1 - Number(v.razresitev.delez)))}
        </td>
        <td>
          <span className={v.razresitev.vrsta === 'nerazvrsceno' ? 'nerazvrsceno' : undefined}>{vir(v)}</span>
          {v.raven === 3 && v.nastavitev !== null && (
            <>
              {' '}
              <button
                type="button"
                className="povezava-gumb"
                disabled={shranjujem}
                onClick={() => {
                  nastavi(v.konto, '');
                }}
              >
                odstrani izjemo
              </button>
            </>
          )}
        </td>
        <td className="predlog-fcvc">
          <div className="predlog-vsebina">
            {v.raven === 2 && v.razclenitev && <span className="opozorilo-crta">Priporočena razčlenitev na 3-mestne konte</span>}
            {v.predlog !== null && (
              <>
                <span>
                  <strong>{predlogKratko(v.predlog)}</strong> · {ZANESLJIVOST[v.predlog.zanesljivost]}
                  {v.predlog.odstopa && <span className="oznaka-spremembe"> · {oznakaOdstopanja(v)}</span>}
                </span>
                <span className="drobno">{razlogPredloga(v.predlog)}</span>
                <span className="drobno mere">{merePredloga(v.predlog)}</span>
                {velja(v) ? (
                  <span className="drobno">predlog že velja</span>
                ) : (
                  v.predlog.predlog !== null && (
                    <button
                      type="button"
                      className="povezava-gumb"
                      disabled={shranjujem}
                      aria-label={`Prevzemi predlog ${v.konto}`}
                      onClick={() => {
                        prevzemi(v.konto, v.predlog!.predlog!);
                      }}
                    >
                      Prevzemi predlog
                    </button>
                  )
                )}
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  return (
    <main className="siroko">
      <div className="vsebina">
        <nav className="drobtine">
          <Link to="/">Podjetja</Link>
          <Link to={`/podjetje/${id}`}>{nazivPodjetja || 'Podjetje'}</Link>
        </nav>

        <h1>Fiksni in variabilni stroški</h1>
        <p className="podnaslov">
          Delež variabilnih stroškov po kontih za analizo občutljivosti (Kazalniki). Nastavitev
          2-mestnega konta velja za vse njegove 3-mestne konte; 3-mestni konto ima lahko svojo
          izjemo. Konti so šifre naše sheme. Fiksni delež je preostanek do 100 %.
        </p>

        {napaka !== null && <p className="napaka">{napaka}</p>}
        {nastavitve === null && napaka === null && <p>Berem nastavitve …</p>}

        {nastavitve !== null && (
          <>
            {n !== null && n.stevilo > 0 && (
              <section className="kartica fcvc-opozorilo" aria-label="Nerazvrščeni stroški">
                <p>
                  <Lupa /> <strong>{`Zadnje celo leto (${String(n.leto)}):`}</strong>{' '}
                  {`${decimalno(String(Number(n.delez ?? 0) * 100), 1)} % stroškov (${znesek(n.znesek)} €) še nima določene razvrstitve na fiksni in variabilni del. V izračunu so obravnavani kot fiksni, zato so rezultati približni. Priporočamo, da razvrstitev pregledate skupaj s svetovalcem.`}
                </p>
                <ul>
                  {n.najvecji.map((k) => (
                    <li key={k.konto}>
                      <code>{k.konto}</code> {k.naziv} · {znesek(k.znesek)} €
                    </li>
                  ))}
                </ul>
                {n.nadPragomVZadnjemCelemLetu && (
                  <p className="opozorilo">
                    {`V letu ${String(n.leto)} je nerazvrščenih več kot ${decimalno(String(Number(n.prag) * 100), 0)} % stroškov: za to leto analiza občutljivosti ne bo prikazana (»Analiza še ni dovolj nastavljena«), dokler jih ne razvrstite. Za vsako drugo obdobje analiza prag preveri sama.`}
                  </p>
                )}
              </section>
            )}

            <section className="kartica">
              <p className="namig">
                {leto === null
                  ? 'Podjetje še nima uvoženega celega leta; zneski niso prikazani.'
                  : `Znesek je strošek leta ${String(leto)}.`}{' '}
                {nastavitve.okno === null
                  ? ''
                  : `Prikazani so konti s stroškom v zadnjih ${String(nastavitve.okno.mesecev)} mesecih (${nastavitve.okno.od} – ${nastavitve.okno.do}).`}{' '}
                {nastavitve.predlog === null
                  ? ''
                  : `Predlog iz zgodovine: ${String(nastavitve.predlog.mesecev)} mesecev (${nastavitve.predlog.od} – ${nastavitve.predlog.do})${nastavitve.predlog.pokritost === null ? '' : `, s predlogom je ${decimalno(String(Number(nastavitve.predlog.pokritost) * 100), 1)} % stroškov`}. Predlog se nikoli ne uporabi sam.`}
              </p>
              <label className="stikalo">
                <input
                  type="checkbox"
                  checked={vse}
                  onChange={(e) => {
                    setVse(e.target.checked);
                  }}
                />{' '}
                pokaži vse konte
              </label>
              {dvomestni.length === 0 ? (
                <p>Ni stroškovnih kontov.</p>
              ) : (
                <div className="fcvc-ovoj">
                  <table className="nastavitve fcvc">
                    <thead>
                      <tr>
                        <th>Konto</th>
                        <th>Naziv</th>
                        <th className="stevilka">{leto === null ? 'Znesek' : `Znesek ${String(leto)}`}</th>
                        <th>VC %</th>
                        <th className="stevilka">FC %</th>
                        <th>Vir</th>
                        <th>Predlog iz zgodovine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dvomestni.flatMap((v) => [
                        vrstica(v),
                        ...(odprti.has(v.konto) ? vidne.filter((p) => p.nadrejeni === v.konto).map(vrstica) : []),
                      ])}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}
